// react-app/src/utils/separateSVGExport.js
import * as THREE from 'three';

/**
 * Separate SVG export utilities for Laser Cut Interlocking components
 * Export vertical and horizontal contours separately with their respective slots
 * Similar to the HTML version workflow
 */

/**
 * Helper function to create a rectangle path from a slot
 */
const createSlotPath = (start, end, thickness) => {
  // Calculate the direction vector
  const direction = new THREE.Vector3().subVectors(end, start).normalize();
  
  // Calculate perpendicular vector (90 degrees rotation)
  const perpendicular = new THREE.Vector3(-direction.y, direction.x, 0);
  
  // Create the four corners of the rectangle
  const halfThickness = thickness / 2;
  const p1 = new THREE.Vector3().addVectors(start, perpendicular.clone().multiplyScalar(halfThickness));
  const p2 = new THREE.Vector3().subVectors(start, perpendicular.clone().multiplyScalar(halfThickness));
  const p3 = new THREE.Vector3().subVectors(end, perpendicular.clone().multiplyScalar(halfThickness));
  const p4 = new THREE.Vector3().addVectors(end, perpendicular.clone().multiplyScalar(halfThickness));
  
  // Create SVG path
  return `M ${p1.x},${p1.y} L ${p2.x},${p2.y} L ${p3.x},${p3.y} L ${p4.x},${p4.y} Z`;
};

/**
 * Export vertical contour shapes with xpositive slots
 * Outputs: shape_1.svg, shape_2.svg, etc.
 */
export const exportVerticalShapesToSVG = (verticalContoursLines, xpositive, materialThickness = 3) => {
  console.log("Exporting vertical shapes to SVG...");
  console.log("verticalContoursLines:", verticalContoursLines);
  console.log("xpositive:", xpositive);
  
  verticalContoursLines.forEach((shapeLines, index) => {
    // 创建一个 SVG 元素
    const svgNamespace = "http://www.w3.org/2000/svg";
    const svgElement = document.createElementNS(svgNamespace, "svg");

    // 设置 SVG 的大小和视图框
    svgElement.setAttribute("width", "200mm");
    svgElement.setAttribute("height", "200mm");
    svgElement.setAttribute("viewBox", "-100 -100 200 200");

    // 对于 verticalContoursLines 中的每一条线，在 SVG 中创建路径（黑色轮廓）
    shapeLines.forEach((shape) => {
      if (!shape || !shape.points) return;
      
      const pathElement = document.createElementNS(svgNamespace, "path");

      // 构造路径数据 (d 属性) - 使用 x 和 y 坐标
      let pathData = '';
      shape.points.forEach((point, i) => {
        if (i === 0) {
          pathData += `M ${point.x},${point.y}`;
        } else {
          pathData += ` L ${point.x},${point.y}`;
        }
      });
      pathData += ' Z'; // 闭合路径
      
      pathElement.setAttribute("d", pathData);
      pathElement.setAttribute("stroke", "black");
      pathElement.setAttribute("fill", "none");
      pathElement.setAttribute("stroke-width", "0.5");

      svgElement.appendChild(pathElement);
    });

    // 只添加属于当前垂直平面的槽
    let slotCount = 0;
    xpositive.forEach((group) => {
      // 检查userData中的平面索引
      if (group.userData && group.userData.verticalPlaneIndex === index) {
        if (group.children && Array.isArray(group.children)) {
          group.children.forEach((line) => {
            if (!line || !line.geometry) return;
            
            const positions = line.geometry.attributes.position;
            if (!positions || positions.count < 2) return;

            // Get start and end points
            const start = new THREE.Vector3(
              positions.getX(0),
              positions.getY(0),
              positions.getZ(0)
            );
            const end = new THREE.Vector3(
              positions.getX(positions.count - 1),
              positions.getY(positions.count - 1),
              positions.getZ(positions.count - 1)
            );

            // Create rectangle (slot) path
            const slotPath = createSlotPath(start, end, materialThickness);
            
            const pathElement = document.createElementNS(svgNamespace, "path");
            pathElement.setAttribute("d", slotPath);
            pathElement.setAttribute("stroke", "red");
            pathElement.setAttribute("fill", "none");
            pathElement.setAttribute("stroke-width", "0.5");

            svgElement.appendChild(pathElement);
            slotCount++;
          });
        }
      }
    });

    console.log(`Vertical plane ${index} has ${slotCount} slots`);

    // 将 SVG 元素转换为字符串
    const svgString = new XMLSerializer().serializeToString(svgElement);

    // 创建 Blob 对象并生成下载链接
    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    // 创建下载链接
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = `vertical_plane_${index + 1}.svg`;
    document.body.appendChild(downloadLink);
    downloadLink.click();

    // 清理
    setTimeout(() => {
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
    }, 100);
  });
};

/**
 * Export horizontal contour shapes with xneg slots
 * Outputs: horizontal_plane_1.svg, horizontal_plane_2.svg, etc.
 */
export const exportHorizontalShapesToSVG = (horizontalContoursLines, xneg, lineGroups, materialThickness = 3) => {
  console.log("Exporting horizontal shapes to SVG...");
  console.log("horizontalContoursLines:", horizontalContoursLines);
  console.log("xneg:", xneg);

  horizontalContoursLines.forEach((contourGroup, groupIndex) => {
    // 创建一个新的SVG元素
    const svgNamespace = "http://www.w3.org/2000/svg";
    const svgElement = document.createElementNS(svgNamespace, "svg");

    // 设置SVG的大小和视图框
    svgElement.setAttribute("width", "200mm");
    svgElement.setAttribute("height", "200mm");
    svgElement.setAttribute("viewBox", "-100 -100 200 200");

    // 添加horizontalContoursLines中的形状（黑色轮廓）
    if (contourGroup && Array.isArray(contourGroup)) {
      contourGroup.forEach(shape => {
        if (!shape || !shape.points) return;
        
        const pathElement = document.createElementNS(svgNamespace, "path");
        
        // 构造路径数据 - 使用 x 和 y 坐标
        let pathData = '';
        shape.points.forEach((point, i) => {
          if (i === 0) {
            pathData += `M ${point.x},${point.y}`;
          } else {
            pathData += ` L ${point.x},${point.y}`;
          }
        });
        pathData += ' Z'; // 闭合路径
        
        pathElement.setAttribute("d", pathData);
        pathElement.setAttribute("stroke", "black");
        pathElement.setAttribute("fill", "none");
        pathElement.setAttribute("stroke-width", "0.5");
        svgElement.appendChild(pathElement);
      });
    }

    // 只添加属于当前水平平面的槽
    let slotCount = 0;
    xneg.forEach((group) => {
      // 检查userData中的平面索引
      if (group.userData && group.userData.horizontalPlaneIndex === groupIndex) {
        if (group.children && Array.isArray(group.children)) {
          group.children.forEach((line) => {
            if (!line || !line.geometry) return;
            
            const positions = line.geometry.attributes.position;
            if (!positions || positions.count < 2) return;
            
            // Get start and end points - 注意这里使用x和z坐标，因为是水平平面
            const start = new THREE.Vector3(
              positions.getX(0),
              0,  // 对于2D投影，y坐标设为0
              positions.getZ(0)
            );
            const end = new THREE.Vector3(
              positions.getX(positions.count - 1),
              0,
              positions.getZ(positions.count - 1)
            );
            
            // 创建矩形路径
            const slotPath = createSlotPath(
              new THREE.Vector3(start.x, start.z, 0), // 转换为2D坐标
              new THREE.Vector3(end.x, end.z, 0),     // 转换为2D坐标
              materialThickness
            );
            
            const pathElement = document.createElementNS(svgNamespace, "path");
            pathElement.setAttribute("d", slotPath);
            pathElement.setAttribute("stroke", "green");
            pathElement.setAttribute("fill", "none");
            pathElement.setAttribute("stroke-width", "0.5");
            svgElement.appendChild(pathElement);
            slotCount++;
          });
        }
      }
    });

    console.log(`Horizontal plane ${groupIndex} has ${slotCount} slots`);

    // 将SVG元素转换为字符串
    const svgString = new XMLSerializer().serializeToString(svgElement);

    // 创建Blob对象并生成下载链接
    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    // 创建下载链接
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = `horizontal_plane_${groupIndex + 1}.svg`;
    document.body.appendChild(downloadLink);
    downloadLink.click();

    // 清理
    setTimeout(() => {
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
    }, 100);
  });
};

/**
 * Main export function that handles both vertical and horizontal exports
 * Similar to the HTML version workflow
 */
export const exportSeparateSVGs = (horizontalContoursLines, verticalContoursLines, xneg, xpositive, lineGroups, materialThickness = 3) => {
  console.log("Export SVG button clicked");
  console.log("Data received:", {
    horizontalContoursLines,
    verticalContoursLines,
    xneg,
    xpositive,
    lineGroups,
    materialThickness
  });

  if (horizontalContoursLines && xneg) {
    exportHorizontalShapesToSVG(horizontalContoursLines, xneg, lineGroups, materialThickness);
  } else {
    console.error("horizontalContoursLines or xneg is not defined");
  }

  // Export vertical shapes after a delay (matching the HTML version timing)
  setTimeout(() => {
    if (verticalContoursLines && xpositive) {
      exportVerticalShapesToSVG(verticalContoursLines, xpositive, materialThickness);
    } else {
      console.error("verticalContoursLines or xpositive is not defined");
    }
  }, 1000);
};

/**
 * Simplified wrapper for React component usage
 */
export const handleSeparateExport = ({
  horizontalContours,
  verticalContours,
  xpositive,
  xneg,
  lineGroups = [],
  materialThickness = 3
}) => {
  // Convert data format if needed
  const horizontalArray = Array.isArray(horizontalContours) 
    ? horizontalContours 
    : Object.keys(horizontalContours)
        .filter(key => !isNaN(parseInt(key)))
        .map(key => horizontalContours[key]);
        
  const verticalArray = Array.isArray(verticalContours) 
    ? verticalContours 
    : Object.keys(verticalContours)
        .filter(key => !isNaN(parseInt(key)))
        .map(key => verticalContours[key]);

  // Call the export function with proper array format
  exportSeparateSVGs(horizontalArray, verticalArray, xneg, xpositive, lineGroups, materialThickness);
};